/**
 * Stato della sessione di supporto live: WebSocket di signaling + macchina a stati.
 *
 * Il provider non rende nulla: la UI (Task 8) consuma `useSupport()`.
 */
import * as Sentry from "@sentry/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { getSupportSession, supportWsUrl, type SupportSession } from "@/api/support";
import { useAuth } from "@/auth/useAuth";

import { CondivisioneNegata, MediaBloccato, SupportPeer } from "./peer";

export type SupportPhase = "idle" | "calling" | "incoming" | "connected" | "ended";

/** Tetto all'attesa fra due tentativi. NON un tetto al numero di tentativi:
 *  arrendersi lasciava l'utente su una pagina viva ma senza canale, senza
 *  saperlo (incidente 2026-07-29). Si riprova finche' la pagina e' aperta. */
const ATTESA_MAX_MS = 30_000;
/** Dopo quanti tentativi falliti segnalare a Sentry (una volta sola). */
const TENTATIVI_PRIMA_DI_SEGNALARE = 3;

export interface IncomingCall {
  callId: string;
  affiliatoNome: string;
  contesto: string | null;
}

interface SupportContextValue {
  enabled: boolean;
  phase: SupportPhase;
  supervisoreNome: string | null;
  supervisoreOnline: boolean;
  incoming: IncomingCall | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  screenOn: boolean;
  micOn: boolean;
  camOn: boolean;
  errore: string | null;
  expiresAt: string | null;
  /** `invite`/`accept` in volo: la UI deve disabilitare il bottone finche' e' true. */
  richiestaInCorso: boolean;
  /** WebSocket di signaling aperta. Se false non si puo' ne' chiamare ne' ricevere. */
  connesso: boolean;
  /** Forza un tentativo di riconnessione immediato, senza attendere il backoff. */
  riconnettiOra: () => void;
  invite: (contesto?: string | null) => Promise<void>;
  cancel: () => void;
  accept: () => Promise<void>;
  decline: () => void;
  hangup: () => void;
  toggleScreen: () => Promise<void>;
  setMic: (on: boolean) => void;
  setCam: (on: boolean) => void;
  dismissErrore: () => void;
}

const SupportContext = createContext<SupportContextValue | null>(null);

const MESSAGGI_ERRORE: Record<string, string> = {
  no_supervisor: "Nessun supervisore assegnato: chiedi all'amministratore di collegartene uno.",
  unreachable: "Supervisore non raggiungibile in questo momento. Riprova più tardi.",
  busy: "Il supervisore è già in una chiamata di supporto. Riprova tra qualche minuto.",
  already_in_call: "Sei già in una chiamata di supporto: chiudila prima di aprirne un'altra.",
  not_allowed:
    "Non sei autorizzato su questa chiamata: probabilmente è già stata chiusa. Ricarica la pagina e richiama.",
  bad_state: "La richiesta non è più valida. Riprova.",
  internal: "Errore interno del supporto live. Riprova; se continua, avvisa l'amministratore.",
  disabled: "Il supporto live non è attivo: chiedi all'amministratore di abilitarlo.",
};

/** Un messaggio per OGNI `reason` che il backend consegna, incluse le due chiusure
 * volontarie (hangup/cancelled): senza, la vista chiamata sparirebbe senza una parola,
 * indistinguibile da un crash. I testi sono neutri rispetto al destinatario, perche'
 * `timeout` arriva a entrambi i peer e `hangup` a chiunque non abbia riagganciato. */
const MESSAGGI_FINE: Record<string, string> = {
  timeout: "Nessuna risposta entro il tempo previsto: la richiesta di supporto è scaduta.",
  declined: "Richiesta di supporto rifiutata. Riprova più tardi o usa il telefono.",
  hangup: "Chiamata terminata.",
  cancelled: "Richiesta di supporto annullata.",
  peer_gone:
    "Collegamento interrotto: l'altra persona non è più raggiungibile. Puoi riprovare a chiamare.",
  superseded: "Chiamata chiusa: è arrivata una nuova richiesta di supporto.",
  ice_failed:
    "Collegamento audio/video non riuscito: le due reti non riescono a parlarsi direttamente. Riprova, e se continua usa il telefono.",
};

export function SupportProvider({ children }: { children: ReactNode }) {
  const { hasRole } = useAuth();
  const isStaff = hasRole("affiliato") || hasRole("operatore");

  const [session, setSession] = useState<SupportSession | null>(null);
  const [phase, setPhase] = useState<SupportPhase>("idle");
  const [incoming, setIncoming] = useState<IncomingCall | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [screenOn, setScreenOn] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [richiestaInCorso, setRichiestaInCorso] = useState(false);
  const [connesso, setConnesso] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const peerRef = useRef<SupportPeer | null>(null);
  const callIdRef = useRef<string | null>(null);
  const iceRef = useRef<RTCIceServer[]>([]);
  const reconnectRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const apriRef = useRef<(() => void) | null>(null);
  const segnalatoRef = useRef(false);
  /** Doppione di `richiestaInCorso` leggibile senza aspettare il re-render: il
   * guard di idempotenza deve valere anche fra due click nello stesso tick. */
  const inVoloRef = useRef(false);

  /** True se il messaggio e' stato effettivamente inviato (WS aperta). */
  const send = useCallback((msg: Record<string, unknown>): boolean => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }, []);

  const teardown = useCallback(() => {
    peerRef.current?.close();
    peerRef.current = null;
    callIdRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setScreenOn(false);
    setMicOn(true);
    setCamOn(true);
    setExpiresAt(null);
    setIncoming(null);
    setPhase("idle");
  }, []);

  const makePeer = useCallback((): SupportPeer => {
    // Mai sostituire un peer senza chiuderlo: quello precedente ha webcam e
    // microfono acquisiti e, diventato irraggiungibile, resterebbe con la spia
    // accesa fino al reload della pagina.
    peerRef.current?.close();
    const peer = new SupportPeer(iceRef.current, {
      onRemoteStream: (stream) => setRemoteStream(stream),
      onSignal: (payload) => {
        if (callIdRef.current) {
          send({ type: "signal", call_id: callIdRef.current, payload });
        }
      },
      onFailed: () => {
        // Senza TURN questo e' lo scenario atteso su NAT simmetrici: va contato.
        Sentry.captureMessage("support: ICE failed");
        setErrore("Connessione non riuscita. Riprova o contatta il supervisore per telefono.");
        // `ice_failed` e non `hangup`: mandare hangup rendeva un guasto di rete
        // indistinguibile da un "Riaggancia" premuto a mano, e nei log del server la
        // causa era illeggibile (incidente 2026-07-29: due chiamate chiuse a 12s
        // sembravano riagganci volontari).
        if (callIdRef.current) send({ type: "ice_failed", call_id: callIdRef.current });
        teardown();
      },
      onScreenEnded: () => setScreenOn(false),
    });
    peerRef.current = peer;
    return peer;
  }, [send, teardown]);

  // Bootstrap: config ICE + stato supervisore
  useEffect(() => {
    if (!isStaff) return;
    let annullato = false;
    getSupportSession()
      .then((s) => {
        if (annullato) return;
        setSession(s);
        iceRef.current = s.ice_servers;
      })
      .catch((err) => Sentry.captureException(err));
    return () => {
      annullato = true;
    };
  }, [isStaff]);

  // Una sola WebSocket per sessione, aperta finche' la feature e' attiva.
  // Riconnessione con backoff esponenziale, come src/chat/ChatContext.tsx.
  useEffect(() => {
    if (!isStaff || !session?.enabled) return;
    if (wsRef.current) return;

    let chiuso = false;

    const apri = () => {
      const ws = new WebSocket(supportWsUrl());
      wsRef.current = ws;
      collega(ws);
    };
    apriRef.current = apri;

    const collega = (ws: WebSocket) => {
      ws.onopen = () => {
        reconnectRef.current = 0;
        segnalatoRef.current = false;
        setConnesso(true);
      };
      ws.onmessage = onMessage;
      ws.onclose = () => {
        wsRef.current = null;
        setConnesso(false);
        if (chiuso) return;
        // Il gate include `peerRef.current` e non solo `callIdRef.current`: fra il
        // click su "Chiedi aiuto" e il `ringing` (che il backend manda DOPO la
        // notifica Telegram, il cui client httpx ha timeout 10s) webcam e microfono
        // sono gia' acquisiti ma la chiamata non ha ancora un id. Un drop in quella
        // finestra non faceva teardown: la spia della webcam restava accesa e il
        // guard di idempotenza (peer vivo) scartava in silenzio ogni click
        // successivo, senza via d'uscita che non fosse ricaricare la pagina.
        // Sulla stessa disconnessione il backend chiude la chiamata (peer_gone) e
        // quel messaggio non puo' piu' arrivare a un socket morto: senza teardown
        // resterebbero vivi SupportPeer e MediaStream davanti a un video congelato,
        // con la fase bloccata su "connected" e nessun modo di uscirne se non
        // "Riaggancia" (che risponderebbe `bad_state`).
        if (callIdRef.current || peerRef.current) {
          setErrore(
            callIdRef.current
              ? "Collegamento al supporto interrotto: la chiamata è stata chiusa."
              : "Collegamento al supporto interrotto: la richiesta non è partita. Riprova.",
          );
          teardown();
        }
        if (reconnectRef.current >= TENTATIVI_PRIMA_DI_SEGNALARE && !segnalatoRef.current) {
          segnalatoRef.current = true;
          Sentry.captureMessage("support: WebSocket ancora chiusa dopo piu' tentativi", {
            level: "warning",
            extra: { tentativi: reconnectRef.current, callId: callIdRef.current },
          });
        }
        const attesa = Math.min(Math.pow(2, reconnectRef.current) * 1000, ATTESA_MAX_MS);
        reconnectRef.current += 1;
        retryTimerRef.current = setTimeout(apri, attesa);
      };
      ws.onerror = () => {
        /* onclose gestisce la riconnessione */
      };
    };

    const onMessage = (event: MessageEvent) => {
      const msg = JSON.parse(event.data as string) as Record<string, unknown>;
      const tipo = msg.type as string;

      if (tipo === "ringing") {
        callIdRef.current = msg.call_id as string;
        setExpiresAt(msg.expires_at as string);
        setPhase("calling");
        return;
      }
      if (tipo === "invite") {
        callIdRef.current = msg.call_id as string;
        setExpiresAt(msg.expires_at as string);
        setIncoming({
          callId: msg.call_id as string,
          affiliatoNome: msg.affiliato_nome as string,
          contesto: (msg.contesto as string | null) ?? null,
        });
        setPhase("incoming");
        return;
      }
      if (tipo === "accepted") {
        // Il peer e il media locale esistono gia': li ha creati invite().
        setPhase("connected");
        void peerRef.current?.createOffer();
        return;
      }
      if (tipo === "signal") {
        void peerRef.current?.handleSignal(msg.payload);
        return;
      }
      if (tipo === "ended") {
        const reason = msg.reason as string;
        if (MESSAGGI_FINE[reason]) setErrore(MESSAGGI_FINE[reason]);
        teardown();
        return;
      }
      if (tipo === "error") {
        setErrore(MESSAGGI_ERRORE[msg.code as string] ?? "Errore imprevisto.");
        teardown();
      }
    };

    apri();

    return () => {
      chiuso = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
      apriRef.current = null;
      setConnesso(false);
      const ws = wsRef.current;
      if (ws) {
        ws.onclose = null;
        ws.close();
      }
      wsRef.current = null;
      // Chiamata diretta sul ref (non `teardown()`, che farebbe setState dopo lo
      // smontaggio): senza, webcam/microfono restano accesi a tempo indeterminato
      // se il provider si smonta (logout, cambio route) a chiamata in corso.
      peerRef.current?.close();
      peerRef.current = null;
    };
    // `localStream` deliberatamente fuori dalle deps: leggerlo qui ricreerebbe la
    // WebSocket a ogni cambio di stream (deps-churn). Si usa la ref.
  }, [isStaff, session?.enabled, makePeer, teardown]);

  /** Crea il peer e acquisisce il media. False se l'utente ha negato tutto. */
  const preparaMedia = useCallback(async (): Promise<boolean> => {
    const peer = makePeer();
    try {
      setLocalStream(await peer.startLocalMedia());
    } catch (err) {
      peer.close();
      peerRef.current = null;
      if (err instanceof MediaBloccato) {
        setErrore(
          "Webcam e microfono non disponibili: consenti l'accesso dal browser per usare il supporto live.",
        );
      } else {
        Sentry.captureException(err);
        setErrore("Non riesco ad accedere a webcam o microfono.");
      }
      return false;
    }
    if (!peer.videoDisponibile) {
      setErrore("Webcam non disponibile: la chiamata prosegue con audio e schermo.");
    }
    return true;
  }, [makePeer]);

  /** Chiude il peer e azzera il media appena acquisiti: usato quando l'invio fallisce
   * (WS non OPEN, es. finestra di backoff) per non lasciare webcam/mic accesi a vuoto. */
  const abbandonaMediaAppenaAcquisiti = useCallback(() => {
    peerRef.current?.close();
    peerRef.current = null;
    setLocalStream(null);
  }, []);

  /** Guard di idempotenza: un peer vivo o un'operazione in volo bloccano la seconda
   * chiamata. `invite` resta "invisibile" (fase ancora idle) finche' non torna
   * `ringing`, cioe' per 1-3s dopo il click: senza guard il secondo click creava un
   * secondo peer, lasciava il primo orfano con webcam accesa e faceva partire due
   * notifiche Telegram, la prima con un deep-link a una chiamata gia' morta. */
  const iniziaOperazione = useCallback((): boolean => {
    if (peerRef.current || inVoloRef.current) return false;
    inVoloRef.current = true;
    setRichiestaInCorso(true);
    return true;
  }, []);

  const fineOperazione = useCallback(() => {
    inVoloRef.current = false;
    setRichiestaInCorso(false);
  }, []);

  const invite = useCallback(
    async (contesto: string | null = null) => {
      if (!iniziaOperazione()) return;
      setErrore(null);
      try {
        if (!(await preparaMedia())) return;
        if (!send({ type: "invite", contesto })) {
          abbandonaMediaAppenaAcquisiti();
          setErrore("Connessione al supporto non attiva: attendi qualche secondo e riprova.");
        }
      } finally {
        fineOperazione();
      }
    },
    [iniziaOperazione, fineOperazione, preparaMedia, send, abbandonaMediaAppenaAcquisiti],
  );

  const accept = useCallback(async () => {
    if (!callIdRef.current) return;
    if (!iniziaOperazione()) return;
    setErrore(null);
    try {
      if (!(await preparaMedia())) return;
      if (!send({ type: "accept", call_id: callIdRef.current })) {
        abbandonaMediaAppenaAcquisiti();
        setErrore("Connessione al supporto non attiva: attendi qualche secondo e riprova.");
        return;
      }
      setIncoming(null);
      setPhase("connected");
    } finally {
      fineOperazione();
    }
  }, [iniziaOperazione, fineOperazione, preparaMedia, send, abbandonaMediaAppenaAcquisiti]);

  const closeWith = useCallback(
    (type: string) => {
      if (callIdRef.current) send({ type, call_id: callIdRef.current });
      teardown();
    },
    [send, teardown],
  );

  // Ref callback e non useEffect+useRef: l'elemento nasce e muore con lo stream, e un
  // effetto con deps [remoteStream] non ripartirebbe alla comparsa dell'elemento.
  const agganciaAudio = useCallback(
    (el: HTMLAudioElement | null) => {
      if (el) el.srcObject = remoteStream;
    },
    [remoteStream],
  );

  const value: SupportContextValue = {
    enabled: Boolean(session?.enabled),
    phase,
    supervisoreNome: session?.supervisore_nome ?? null,
    supervisoreOnline: Boolean(session?.supervisore_online),
    incoming,
    localStream,
    remoteStream,
    screenOn,
    micOn,
    camOn,
    errore,
    expiresAt,
    richiestaInCorso,
    connesso,
    riconnettiOra: () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
      reconnectRef.current = 0;
      if (!wsRef.current) apriRef.current?.();
    },
    invite,
    cancel: () => closeWith("cancel"),
    accept,
    decline: () => closeWith("decline"),
    hangup: () => closeWith("hangup"),
    toggleScreen: async () => {
      // Senza questo try/catch un picker annullato lasciava una promise rifiutata
      // non gestita ("DOMException: The request is not allowed...") e nessuna
      // spiegazione a schermo. Su Firefox e' il caso normale, non un guasto.
      try {
        const on = await peerRef.current?.toggleScreen();
        setScreenOn(Boolean(on));
      } catch (err) {
        setScreenOn(false);
        setErrore(
          err instanceof CondivisioneNegata
            ? "Condivisione dello schermo annullata. Puoi riprovare quando vuoi."
            : "Condivisione dello schermo non riuscita. Riprova.",
        );
        if (!(err instanceof CondivisioneNegata)) Sentry.captureException(err);
      }
    },
    setMic: (on) => {
      peerRef.current?.setMicEnabled(on);
      setMicOn(on);
    },
    setCam: (on) => {
      peerRef.current?.setCamEnabled(on);
      setCamOn(on);
    },
    dismissErrore: () => setErrore(null),
  };

  return (
    <SupportContext.Provider value={value}>
      {children}
      {/* L'audio remoto vive QUI, fuori dalle rotte, e non in una vista: il <video> di
          CallView stava dentro /supporto, quindi minimizzando la chiamata nella barra
          React lo smontava e la chiamata restava muta (incidente 2026-07-29). Il video
          della barra e' muted, e sotto sm e' display:none: nessuna vista puo' essere la
          sorgente dell'audio. Montato insieme allo stream, cosi' autoPlay parte davvero
          invece di trovare un srcObject ancora nullo. */}
      {remoteStream && <audio ref={agganciaAudio} autoPlay />}
    </SupportContext.Provider>
  );
}

export function useSupport(): SupportContextValue {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error("useSupport deve stare dentro SupportProvider");
  return ctx;
}

/** Come `useSupport` ma tollera l'assenza del provider (null): serve a chi vive fuori
 * dall'albero del provider in isolamento, es. la Sidebar nei suoi test unitari. */
export function useSupportOptional(): SupportContextValue | null {
  return useContext(SupportContext);
}
